/**
 * chartMath.js
 * Pure utility functions for natal chart computation.
 * No React, no DOM — pure math.
 */

// ─── Signs ───────────────────────────────────────────────────────────────────

export const SIGNS = [
  { name: 'Aries',       abbr: 'ARI', glyph: '♈', element: 'fire',  mode: 'cardinal' },
  { name: 'Taurus',      abbr: 'TAU', glyph: '♉', element: 'earth', mode: 'fixed'    },
  { name: 'Gemini',      abbr: 'GEM', glyph: '♊', element: 'air',   mode: 'mutable'  },
  { name: 'Cancer',      abbr: 'CAN', glyph: '♋', element: 'water', mode: 'cardinal' },
  { name: 'Leo',         abbr: 'LEO', glyph: '♌', element: 'fire',  mode: 'fixed'    },
  { name: 'Virgo',       abbr: 'VIR', glyph: '♍', element: 'earth', mode: 'mutable'  },
  { name: 'Libra',       abbr: 'LIB', glyph: '♎', element: 'air',   mode: 'cardinal' },
  { name: 'Scorpio',     abbr: 'SCO', glyph: '♏', element: 'water', mode: 'fixed'    },
  { name: 'Sagittarius', abbr: 'SAG', glyph: '♐', element: 'fire',  mode: 'mutable'  },
  { name: 'Capricorn',   abbr: 'CAP', glyph: '♑', element: 'earth', mode: 'cardinal' },
  { name: 'Aquarius',    abbr: 'AQU', glyph: '♒', element: 'air',   mode: 'fixed'    },
  { name: 'Pisces',      abbr: 'PIS', glyph: '♓', element: 'water', mode: 'mutable'  },
];

// ─── Aspects ──────────────────────────────────────────────────────────────────

export const ASPECTS = [
  { name: 'conjunction', angle: 0,   orb: 8, lineStyle: 'solid'    },
  { name: 'sextile',     angle: 60,  orb: 6, lineStyle: 'dotted'   },
  { name: 'square',      angle: 90,  orb: 8, lineStyle: 'dashed'   },
  { name: 'trine',       angle: 120, orb: 8, lineStyle: 'dot-dash' },
  { name: 'opposition',  angle: 180, orb: 8, lineStyle: 'solid'    },
];

// ─── longitudeToSign ─────────────────────────────────────────────────────────

/**
 * Convert an ecliptic longitude (0–360) to sign data.
 * @param {number} longitude - Ecliptic longitude in degrees (0–360).
 * @returns {{ sign: object, signIndex: number, degree: number, minutes: number, totalDegrees: number }}
 */
export function longitudeToSign(longitude) {
  const normalized = ((longitude % 360) + 360) % 360;
  const signIndex = Math.floor(normalized / 30);
  const positionInSign = normalized - signIndex * 30;
  const degree = Math.floor(positionInSign);
  const minutes = Math.floor((positionInSign - degree) * 60);

  return {
    sign: SIGNS[signIndex],
    signIndex,
    degree,
    minutes,
    totalDegrees: normalized,
  };
}

// ─── formatPosition ───────────────────────────────────────────────────────────

/**
 * Format a planet glyph + longitude for display.
 * Example: "☉ 15 ARI"
 * @param {string} glyph - Planet glyph unicode character.
 * @param {number} longitude - Ecliptic longitude in degrees.
 * @returns {string}
 */
export function formatPosition(glyph, longitude) {
  const { sign, degree } = longitudeToSign(longitude);
  return `${glyph} ${degree} ${sign.abbr}`;
}

// ─── isNearBoundary ───────────────────────────────────────────────────────────

/**
 * Check if a longitude is within `threshold` degrees of a sign boundary.
 * @param {number} longitude - Ecliptic longitude in degrees.
 * @param {number} [threshold=1] - Degrees from boundary to consider "near".
 * @returns {boolean}
 */
export function isNearBoundary(longitude, threshold = 1) {
  const normalized = ((longitude % 360) + 360) % 360;
  const positionInSign = normalized % 30;
  return positionInSign < threshold || positionInSign > 30 - threshold;
}

// ─── detectAspect ─────────────────────────────────────────────────────────────

/**
 * Detect the aspect (if any) between two ecliptic longitudes.
 * @param {number} lon1 - First longitude in degrees.
 * @param {number} lon2 - Second longitude in degrees.
 * @returns {{ name: string, angle: number, orb: number, lineStyle: string, separation: number } | null}
 *   The matched aspect with the actual separation, or null if none.
 */
export function detectAspect(lon1, lon2) {
  const diff = Math.abs(lon1 - lon2) % 360;
  const separation = diff > 180 ? 360 - diff : diff;

  for (const aspect of ASPECTS) {
    if (Math.abs(separation - aspect.angle) <= aspect.orb) {
      return { ...aspect, separation };
    }
  }
  return null;
}

// ─── getAllAspects ────────────────────────────────────────────────────────────

/**
 * Get all aspects between an array of placed planets.
 * @param {{ id: string, longitude: number }[]} planets - Array of planet objects.
 * @returns {{ planet1: string, planet2: string, aspect: object }[]}
 *   Array of aspect objects, one per pair that forms an aspect.
 */
export function getAllAspects(planets) {
  const aspects = [];

  for (let i = 0; i < planets.length; i++) {
    for (let j = i + 1; j < planets.length; j++) {
      const aspect = detectAspect(planets[i].longitude, planets[j].longitude);
      if (aspect) {
        aspects.push({
          planet1: planets[i].id,
          planet2: planets[j].id,
          aspect,
        });
      }
    }
  }

  return aspects;
}

// ─── longitudeToSvgPoint ─────────────────────────────────────────────────────

/**
 * Convert an ecliptic longitude to an SVG (x, y) point on a circle.
 * 0° Aries is at the top (12 o'clock), increasing clockwise.
 *
 * @param {number} longitude - Ecliptic longitude in degrees (0–360).
 * @param {number} cx - Circle center x.
 * @param {number} cy - Circle center y.
 * @param {number} radius - Circle radius.
 * @returns {{ x: number, y: number }}
 */
export function longitudeToSvgPoint(longitude, cx, cy, radius) {
  // SVG 0° is 3 o'clock; we want 0° at 12 o'clock, going clockwise.
  // Subtract 90° to rotate so that 0° maps to top.
  const angleRad = ((longitude - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}
